/**
 * The size of every Open Graph image the site can reference.
 *
 * ``og:image:width`` and ``og:image:height`` tell a social scraper how big the
 * card art is before it has downloaded it. Both emission sites —
 * ``scripts/generate-static-meta.mjs`` for crawlers and
 * :mod:`src/components/LanguageRouter` for the runtime ``<head>`` — import the
 * constant from here, so neither can restate the number and invariant ``I4``
 * gains no new divergence.
 *
 * **One rule, no exceptions — since CWS-118.** The site's policy is that Open
 * Graph art is 1200×630. Until CWS-118 this module also carried
 * ``OG_IMAGE_EXCEPTIONS``, a 23-entry map of the blog thumbnails that departed
 * from it, and ``getOgImageDimensions`` resolved through it. The per-locale
 * blog cards retired those files, and the map was **deleted, never updated** —
 * an entry updated to 1200×630 would still match its file and stay exempt from
 * the standard-size assertion, reading as coverage while covering nothing.
 *
 * **The rule is bound to the art, not trusted.**
 * ``tests-node/seoSocial.test.ts`` measures every image the build references
 * against 1200×630 in both directions, and
 * ``tests-node/blogSocialCards.test.ts`` holds every image under ``public/og/``
 * to a byte signature that matches its extension — an undersized or
 * misdeclared newcomer fails there, naming the file.
 *
 * **No ``@/`` import, no side effects.** ``generate-static-meta.mjs`` loads the
 * head module chain through ``importTsModule``, whose transpiler rewrites
 * **relative** import specifiers only; an aliased import here would break
 * ``npm run build`` while Vite and Vitest stayed green.
 */

/** An image's size in pixels. */
export interface OgImageDimensions {
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
}

/**
 * The size every Open Graph image is expected to be.
 *
 * 1200×630 (1.91:1) is the one size that satisfies all three platforms that
 * matter here: Meta recommends it and renders a small thumbnail below 600×315,
 * LinkedIn requires a minimum of 1200×627, and X's ``summary_large_image``
 * renders it correctly. Confirmed against those vendors' own documentation on
 * 2026-09-09.
 */
export const STANDARD_OG_IMAGE_DIMENSIONS: OgImageDimensions = { width: 1200, height: 630 };
